import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    loginbuttonTextField = "loginbuttonTextField",
    createAccountbuttonTextField = "createAccountbuttonTextField",
}
const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const accountSettings = getSettingsCollection(state).accountSettings;
    const returnUrl = getReturnUrl(state);
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");

    return {
        allowCreateAccount: accountSettings.allowCreateAccount,
        allowGuestCheckout: accountSettings.allowGuestCheckout && !getCurrentUserIsGuest(state) && referredFromShipping,
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentLocation: getLocation(state),
        userName: state.context.session.userName,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.loginbuttonTextField]: string;
        [fields.createAccountbuttonTextField]: string;
        [fields.heading]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasToasterContext & HasHistory & HasProductContext;

export interface PDPRegisterBlockStyles {
    contentContainer?: InjectableCss;
    contentSyles?: TypographyProps;
    loginBtnStyles?: InjectableCss;
    createAccountBtnStyles?: ButtonPresentationProps;
    buttonMainContainer?: InjectableCss;
    headingStyles?: InjectableCss;
    buttonContainer?: InjectableCss;
    registerBlockContainer?: InjectableCss;
}

export const pdpRegisterBlockStyles: PDPRegisterBlockStyles = {
    registerBlockContainer: {
        css: css`
            background-color: #ffffff;
            padding: 16px;
            width: max-content;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    contentSyles: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
            font-size: 12px;
            font-weight: 400;
            line-height: 24px;
            text-align: left;
        `,
    },
    loginBtnStyles: {
        css: css`
            span {
                font-family: Poppins;
                font-size: 12px;
                font-weight: 600;
                line-height: 16px;
                text-align: left;
                color: ${getColor("text.link")};
            }
            &:hover {
                color: ${getColor("text.link")};
                border: none;
                background: none;
            }
        `,
    },
    createAccountBtnStyles: {
        css: css`
            border: none;
            background: white;
            font-size: 12px;
            font-weight: 600;
            line-height: 16px;
            padding: 0px 0px 0px 0px;
            border-radius: 24px;
            color: ${getColor("text.link")};
            text-align: left;
            /* margin: 10px 0px 15px 0px; */
            &:hover {
                background: white !important;
                border: none !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        /* margin: 5px 0px 0px 0px; */
                    `,
                    css``,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    buttonMainContainer: {
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        align-self: center;
                        /* margin-top: 10px; */
                    `,
                    css``,
                    css``,
                    null,
                    null,
                ])}
        `,
    },
    headingStyles: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
            font-size: 12px;
            font-weight: 600;
            line-height: 16px;
            text-align: left;
        `,
    },
    buttonContainer: {
        css: css`
            display: flex;
            align-items: center;
            gap: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 8px;
                    `,
                    css`
                        margin-left: 8px;
                    `,
                    css`
                        justify-content: flex-end;
                    `,
                    css`
                        margin-left: 8px;
                    `,
                    css``,
                ])}
        `,
    },
};

const styles = pdpRegisterBlockStyles;

const CenNewPDPRegisterBlock: FC<Props> = ({
    fields,
    createAccountUrl,
    history,
    signInUrl,
    currentLocation,
    currentUserIsGuest,
    userName,
    variantSelectionCompleted,
    productContext,
}) => {
    const productIsVariantParent = productContext?.product.isVariantParent;
    // const handleCreateNewAccount = () => {
    //     if (!createAccountUrl) {
    //         return;
    //     }

    //     history.push(createAccountUrl);
    // };

    if (!productIsVariantParent && !variantSelectionCompleted) {
        return null;
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if (!signInUrl) {
            logger.warn("No url was found for SignInPage, defaulting to /SignIn");
            history.push("/MyAccount/SignIn");
        } else {
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${signInUrl}`);
            } else {
                history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
            }
        }
        e.preventDefault();
    };

    if (!(!userName || (userName && currentUserIsGuest))) {
        return null;
    }

    return (
        <>
            {variantSelectionCompleted && (
                <StyledWrapper {...styles.registerBlockContainer}>
                    <Typography {...styles.headingStyles} as="p">
                        <Link {...styles.loginBtnStyles} onClick={onSignInHandler}>
                            {fields.loginbuttonTextField}
                        </Link>
                        {fields.heading}
                    </Typography>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps)(withProductContext(CenNewPDPRegisterBlock))),
    definition: {
        group: "Common",
        displayName: "CENNewPdpRegisterBlock",
        fieldDefinitions: [
            {
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.loginbuttonTextField,
                editorTemplate: "TextField",
                displayName: "Link Text",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
